import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../i18n/LanguageContext';
import './Communications.css';

const Communications = () => {
    const { user, hasRole } = useAuth();
    const { t } = useLanguage();
    const navigate = useNavigate();
    const [communications, setCommunications] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        message: '',
        message_type: 'announcement',
        target_type: 'all',
        channels: ['in_app'],
        priority: 'normal'
    });

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [comms, commStats] = await Promise.all([
                api.getCommunications(),
                api.getCommunicationsStats()
            ]);
            setCommunications(comms.data);
            setStats(commStats.data);
        } catch (error) {
            console.error('Error loading communications:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        console.log('🔵 FORM SUBMIT started');

        // Get current form values
        console.log('Current formData:', formData);

        // Extract and validate
        const title = (formData.title || '').trim();
        const message = (formData.message || '').trim();
        const channels = formData.channels || [];

        console.log('Extracted values:');
        console.log('  title:', title, '(length:', title.length, ')');
        console.log('  message:', message, '(length:', message.length, ')');
        console.log('  channels:', channels, '(count:', channels.length, ')');

        // Client-side validation
        if (!title) {
            alert('❌ ' + t('Title is required'));
            console.log('Validation failed: title empty');
            return;
        }
        if (!message) {
            alert('❌ ' + t('Message is required'));
            console.log('Validation failed: message empty');
            return;
        }
        if (channels.length === 0) {
            alert('❌ ' + t('Select at least one channel'));
            console.log('Validation failed: channels empty');
            return;
        }

        console.log('✅ Client validation passed');

        try {
            // Build payload
            const payload = {
                title: title,
                message: message,
                message_type: formData.message_type || 'announcement',
                target_type: formData.target_type || 'all',
                channels: channels,
                priority: formData.priority || 'normal'
            };

            console.log('📦 Payload ready:', payload);
            console.log('📦 Payload as JSON string:', JSON.stringify(payload));

            // Submit
            console.log('🔄 Calling api.createCommunication()...');
            const result = await api.createCommunication(payload);
            console.log('✅ Success! Result:', result);

            // Reset form
            setShowModal(false);
            setFormData({
                title: '',
                message: '',
                message_type: 'announcement',
                target_type: 'all',
                channels: ['in_app'],
                priority: 'normal'
            });

            // Reload data
            await loadData();
            alert('✅ ' + t('Communication created successfully!'));

        } catch (error) {
            console.error('❌ Error:', error);
            const msg = error.response?.data?.message || error.message || t('Unknown error');
            alert('❌ ' + t('Failed to create:') + ' ' + msg);
        }
    };

    const handleSend = async (id) => {
        if (window.confirm(t('Send this communication now?'))) {
            try {
                await api.sendCommunication(id);
                loadData();
            } catch (error) {
                console.error('Error sending communication:', error);
            }
        }
    };

    const handleChannelChange = (channel) => {
        setFormData(prev => ({
            ...prev,
            channels: prev.channels.includes(channel)
                ? prev.channels.filter(c => c !== channel)
                : [...prev.channels, channel]
        }));
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            draft: 'badge-secondary',
            scheduled: 'badge-info',
            sending: 'badge-warning',
            sent: 'badge-success',
            failed: 'badge-danger',
            cancelled: 'badge-secondary'
        };
        return statusMap[status] || 'badge-secondary';
    };

    const messageTypes = [
        { value: 'announcement', label: t('Announcement') },
        { value: 'notification', label: t('Notification') },
        { value: 'meeting', label: t('Meeting') },
        { value: 'training', label: t('Training') },
        { value: 'election', label: t('Election') },
        { value: 'general', label: t('General') }
    ];

    return (
        <div className="communications-page">
            <div className="page-header">
                <button type="button" className="comms-back" aria-label={t('Back')} onClick={() => navigate('/')}>←</button>
                <div className="comms-heading">
                    <span className="comms-eyebrow">TAREWU MEMBERS</span>
                    <h1 className="page-title">{t('Communications & Announcements')}</h1>
                    <p>{t('Announcements, notices and messages to members')}</p>
                </div>
                {hasRole(['admin', 'secretary', 'chair']) && (
                    <button className="btn btn-primary" onClick={() => setShowModal(true)}>
                        + {t('New Message')}
                    </button>
                )}
            </div>

            {/* Stats */}
            {stats && (
                <div className="stats-grid">
                    <div className="stat-card">
                        <div className="stat-icon">📱</div>
                        <div className="stat-value">{stats.sent_this_month || 0}</div>
                        <div className="stat-label">{t('Sent This Month')}</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon">📢</div>
                        <div className="stat-value">
                            {stats.by_type?.find(x => x.message_type === 'announcement')?.count || 0}
                        </div>
                        <div className="stat-label">{t('Announcements')}</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon">📧</div>
                        <div className="stat-value">
                            {stats.by_type?.find(x => x.message_type === 'meeting')?.count || 0}
                        </div>
                        <div className="stat-label">{t('Meetings')}</div>
                    </div>
                </div>
            )}

            {/* Communications List */}
            <div className="card">
                <div className="card-header">
                    <h3 className="card-title">{t('All Communications')}</h3>
                </div>
                {loading ? (
                    <div className="loading">
                        <div className="loading-spinner"></div>
                    </div>
                ) : communications.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-state-icon">📱</div>
                        <h3 className="empty-state-title">{t('No Communications Yet')}</h3>
                        <p>{t('Create your first announcement')}</p>
                    </div>
                ) : (
                    <div className="communications-list">
                        {communications.map((item) => (
                            <div key={item.id} className="communication-item">
                                <div className="communication-header">
                                    <div className="communication-type badge badge-info">
                                        {item.message_type}
                                    </div>
                                    <span className={`badge ${getStatusBadge(item.status)}`}>
                                        {item.status}
                                    </span>
                                </div>
                                <h4 className="communication-title">{item.title}</h4>
                                <p className="communication-preview">{item.message.substring(0, 150)}...</p>
                                <div className="communication-meta">
                                    <span>
                                        {t('Channels:')} {
                                            Array.isArray(item.channels)
                                                ? item.channels.join(', ')
                                                : typeof item.channels === 'string'
                                                ? item.channels
                                                : 'N/A'
                                        }
                                    </span>
                                    <span>
                                        {item.sent_at
                                            ? `${t('Sent:')} ${new Date(item.sent_at).toLocaleDateString()}`
                                            : `${t('Created:')} ${new Date(item.created_at).toLocaleDateString()}`
                                        }
                                    </span>
                                </div>
                                {item.status === 'draft' && (
                                    <button
                                        className="btn btn-sm btn-primary"
                                        onClick={() => handleSend(item.id)}
                                    >
                                        {t('Send Now')}
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* New Communication Modal */}
            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3 className="modal-title">{t('New Communication')}</h3>
                            <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="form-group">
                                    <label className="form-label">{t('Title')}</label>
                                    <input
                                        type="text"
                                        name="title"
                                        className="form-input"
                                        value={formData.title}
                                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">{t('Type')}</label>
                                        <select
                                            name="message_type"
                                            className="form-input"
                                            value={formData.message_type}
                                            onChange={(e) => setFormData({ ...formData, message_type: e.target.value })}
                                        >
                                            {messageTypes.map(type => (
                                                <option key={type.value} value={type.value}>{type.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">{t('Priority')}</label>
                                        <select
                                            name="priority"
                                            className="form-input"
                                            value={formData.priority}
                                            onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                                        >
                                            <option value="low">{t('Low')}</option>
                                            <option value="normal">{t('Normal')}</option>
                                            <option value="high">{t('High')}</option>
                                            <option value="urgent">{t('Urgent')}</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">{t('Channels')}</label>
                                    <div className="checkbox-group">
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={formData.channels.includes('sms')}
                                                onChange={() => handleChannelChange('sms')}
                                            /> {t('SMS')}
                                        </label>
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={formData.channels.includes('email')}
                                                onChange={() => handleChannelChange('email')}
                                            /> {t('Email')}
                                        </label>
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={formData.channels.includes('in_app')}
                                                onChange={() => handleChannelChange('in_app')}
                                            /> {t('In-App')}
                                        </label>
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">{t('Message')}</label>
                                    <textarea
                                        name="message"
                                        className="form-input"
                                        value={formData.message}
                                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                                        rows="5"
                                        required
                                    />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                                    {t('Cancel')}
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    {t('Create & Send Later')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Communications;
